import React, { Component } from "react";
import axios from "axios";
import { v4 as uuid } from "uuid"; // For commonQuestionId and temporary IDs
import ConceptualQuestionManager from "./ConceptualQuestionManager"; // New component

const config = require("../config.json");

export default class MessageBoardAdmin extends Component {
  constructor(props) {
    super(props);
    this.handleNewQuestionLangVersionChange =
      this.handleNewQuestionLangVersionChange.bind(this);
  }

  state = {
    // Stores conceptual questions grouped by commonQuestionId
    // Example: { "q_common_123": [{lang: 'EN', ...}, {lang: 'DA', ...}] }
    conceptualQuestions: {},
    // Form state for adding a new conceptual question with its first language version
    newConceptualQuestionForm: {
      commonQuestionId: uuid(), // Pre-generate for the new conceptual question
      languageCode: "da", // Default language for the first version
      title: "",
      category: "",
      status: "ACTIVE",
      companyId: "",
    },
    loading: false,
    error: null,
  };

  componentDidMount() {
    this.fetchAndGroupQuestions();
  }

  fetchAndGroupQuestions = async () => {
    this.setState({ loading: true, error: null });
    try {
      const res = await axios.get(
        `${config.api.invokeUrl}/messageboard/questions`
      );
      const allLanguageVersions = res.data; // Expects an array of all question items

      const groupedQuestions = allLanguageVersions.reduce((acc, q) => {
        const { commonQuestionId } = q;
        if (!acc[commonQuestionId]) {
          acc[commonQuestionId] = [];
        }
        // Add the PK to the question object if not already there from backend
        // This helps identify it for updates/deletes if PK isn't a top-level attribute
        const questionWithPKInfo = {
          ...q,
          _pkForAdmin: `QUESTION#${q.languageCode}#${q.commonQuestionId}`,
        };
        acc[commonQuestionId].push(questionWithPKInfo);
        return acc;
      }, {});

      this.setState({ conceptualQuestions: groupedQuestions, loading: false });
    } catch (err) {
      console.error("Error fetching questions:", err);
      this.setState({ loading: false, error: "Failed to fetch questions." });
    }
  };

  handleNewQuestionLangVersionChange(event) {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;

    this.setState((prevState) => ({
      newConceptualQuestionForm: {
        ...prevState.newConceptualQuestionForm,
        [name]: value,
      },
    }));
  }

  handleAddConceptualQuestion = async (event) => {
    event.preventDefault();
    this.setState({ loading: true, error: null });
    const { newConceptualQuestionForm } = this.state;

    const params = {
      commonQuestionId: newConceptualQuestionForm.commonQuestionId,
      languageCode: newConceptualQuestionForm.languageCode,
      title: newConceptualQuestionForm.title,
      category: newConceptualQuestionForm.category,
      status: newConceptualQuestionForm.status,
    };

    if (
      newConceptualQuestionForm.companyId &&
      newConceptualQuestionForm.companyId.trim() !== ""
    ) {
      params.companyId = newConceptualQuestionForm.companyId.trim();
    }

    try {
      // POST to create the first language-specific version
      const res = await axios.post(
        `${config.api.invokeUrl}/messageboard/questions`,
        params
      );

      const createdLangVersion = res.data; // Expect backend to return the created item

      this.setState((prevState) => {
        const updatedConceptualQuestions = { ...prevState.conceptualQuestions };
        if (!updatedConceptualQuestions[createdLangVersion.commonQuestionId]) {
          updatedConceptualQuestions[createdLangVersion.commonQuestionId] = [];
        }
        updatedConceptualQuestions[createdLangVersion.commonQuestionId].push({
          ...createdLangVersion,
          _pkForAdmin: `QUESTION#${createdLangVersion.languageCode}#${createdLangVersion.commonQuestionId}`,
        });

        return {
          conceptualQuestions: updatedConceptualQuestions,
          newConceptualQuestionForm: {
            // Reset form for next conceptual question
            commonQuestionId: uuid(),
            languageCode: "da",
            title: "",
            category: "",
            status: "ACTIVE",
            companyId: "", // Reset companyId
          },
          loading: false,
        };
      });
    } catch (err) {
      console.error("Error adding conceptual question:", err);
      this.setState({ loading: false, error: "Failed to add question." });
    }
  };

  // Called by ConceptualQuestionManager when a language version is added to an existing conceptual Q
  handleAddLanguageVersion = async (langVersionData) => {
    this.setState({ loading: true, error: null });

    const { commonQuestionId } = langVersionData;
    const existingVersions = this.state.conceptualQuestions[commonQuestionId];
    let conceptualQuestionCompanyId = null; // Default to no companyId (global)

    if (
      existingVersions &&
      existingVersions.length > 0 &&
      existingVersions[0].companyId
    ) {
      conceptualQuestionCompanyId = existingVersions[0].companyId;
    }

    const payload = {
      ...langVersionData,
      // Add companyId to payload if the conceptual question is company-scoped
      ...(conceptualQuestionCompanyId && {
        companyId: conceptualQuestionCompanyId,
      }),
    };

    try {
      const res = await axios.post(
        `${config.api.invokeUrl}/messageboard/questions`,
        payload
      );
      const createdLangVersion = res.data;

      this.setState((prevState) => {
        const conceptualQuestions = { ...prevState.conceptualQuestions };
        conceptualQuestions[createdLangVersion.commonQuestionId] = [
          ...(conceptualQuestions[createdLangVersion.commonQuestionId] || []),
          {
            ...createdLangVersion,
            _pkForAdmin: `QUESTION#${createdLangVersion.languageCode}#${createdLangVersion.commonQuestionId}`,
          },
        ];
        return { conceptualQuestions, loading: false };
      });
    } catch (err) {
      console.error("Error adding language version:", err);
      this.setState({
        loading: false,
        error: "Failed to add language version.",
      });
    }
  };

  // Called by ConceptualQuestionManager when a language version is updated
  handleUpdateLanguageVersion = async (langVersionData) => {
    this.setState({ loading: true, error: null });
    const {
      commonQuestionId,
      languageCode,
      title,
      category,
      status,
      createdAt,
    } = langVersionData;

    const params = {
      title,
      category,
      status,
      originalCreatedAt: createdAt,
      languageCode,
      commonQuestionId,
    }; // Only send updateable fields

    if (companyId !== undefined) {
      // Allow sending empty string or null to remove companyId
      params.companyId = companyId === "" ? null : companyId; // Backend might expect null to remove
    }

    try {
      await axios.patch(
        `${config.api.invokeUrl}/messageboard/questions`,
        params
      );
      this.setState((prevState) => {
        const conceptualQuestions = { ...prevState.conceptualQuestions };
        const langVersions = conceptualQuestions[commonQuestionId];
        conceptualQuestions[commonQuestionId] = langVersions.map((lv) =>
          lv.languageCode === languageCode ? { ...lv, ...params } : lv
        );
        return { conceptualQuestions, loading: false };
      });
    } catch (err) {
      console.error("Error updating language version:", err);
      this.setState({
        loading: false,
        error: "Failed to update language version.",
      });
    }
  };

  // Called by ConceptualQuestionManager when a language version is deleted
  handleDeleteLanguageVersion = async (languageCode, commonQuestionId) => {
    this.setState({ loading: true, error: null });
    try {
      await axios.delete(
        `${config.api.invokeUrl}/messageboard/questions/${languageCode}/${commonQuestionId}`
      );
      this.setState((prevState) => {
        const conceptualQuestions = { ...prevState.conceptualQuestions };
        const langVersions = conceptualQuestions[commonQuestionId];
        conceptualQuestions[commonQuestionId] = langVersions.filter(
          (lv) => lv.languageCode !== languageCode
        );
        // If all language versions are deleted, remove the conceptual question entry
        if (conceptualQuestions[commonQuestionId].length === 0) {
          delete conceptualQuestions[commonQuestionId];
        }
        return { conceptualQuestions, loading: false };
      });
    } catch (err) {
      console.error("Error deleting language version:", err);
      this.setState({
        loading: false,
        error: "Failed to delete language version.",
      });
    }
  };

  render() {
    const { newConceptualQuestionForm, conceptualQuestions, loading, error } =
      this.state;

    return (
      <>
        <p className="subtitle is-5">
          Manage Message Board Questions (Multilingual)
        </p>
        <br />

        {error && (
          <div className="notification is-danger is-light">{error}</div>
        )}

        <div className="columns is-multiline">
          <div className="column is-full">
            <div className="box">
              <h2 className="subtitle is-4">Add New Conceptual Question</h2>
              <p className="is-size-7 mb-3">
                This will create a new "conceptual question" identified by a
                common ID, along with its first language version. You can add
                more language versions later.
              </p>
              <form onSubmit={this.handleAddConceptualQuestion}>
                <div className="field">
                  <label className="label">
                    Common Question ID (Auto-generated)
                  </label>
                  <div className="control">
                    <input
                      className="input is-static"
                      type="text"
                      value={newConceptualQuestionForm.commonQuestionId}
                      readOnly
                    />
                  </div>
                </div>
                <hr />
                <p className="label">First Language Version Details:</p>
                <div className="field">
                  <label className="label" htmlFor="languageCode">
                    Language Code (da = dansk, en = engelsk)
                  </label>
                  <div className="control">
                    <input
                      id="languageCode"
                      className="input"
                      type="text"
                      name="languageCode"
                      placeholder="e.g., EN, DA, ES"
                      required
                      maxLength="5" // Common practice
                      value={newConceptualQuestionForm.languageCode}
                      onChange={this.handleNewQuestionLangVersionChange}
                    />
                  </div>
                </div>
                <div className="field">
                  <label className="label" htmlFor="title">
                    Title (for this language)
                  </label>
                  <div className="control">
                    <input
                      id="title"
                      className="input"
                      type="text"
                      name="title"
                      placeholder="Enter question title for this language"
                      required
                      value={newConceptualQuestionForm.title}
                      onChange={this.handleNewQuestionLangVersionChange}
                    />
                  </div>
                </div>
                <div className="field">
                  <label className="label" htmlFor="category">
                    Category (for this language version)
                  </label>
                  <div className="control">
                    <input
                      id="category"
                      className="input"
                      type="text"
                      name="category"
                      placeholder="e.g., General, Tech"
                      required
                      value={newConceptualQuestionForm.category}
                      onChange={this.handleNewQuestionLangVersionChange}
                    />
                  </div>
                </div>
                <div className="field">
                  <label className="label" htmlFor="status">
                    Status (for this language version)
                  </label>
                  <div className="control">
                    <div className="select">
                      <select
                        id="status"
                        name="status"
                        value={newConceptualQuestionForm.status}
                        onChange={this.handleNewQuestionLangVersionChange}
                      >
                        <option value="ACTIVE">Active</option>
                        <option value="INACTIVE">Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="field">
                  <label className="label" htmlFor="companyId">
                    Company ID (Optional)
                  </label>
                  <div className="control">
                    <input
                      id="companyId"
                      className="input"
                      type="text"
                      name="companyId"
                      placeholder="Leave blank if not company-specific"
                      value={newConceptualQuestionForm.companyId}
                      onChange={this.handleNewQuestionLangVersionChange}
                    />
                  </div>
                </div>
                <div className="control mt-4">
                  <button
                    type="submit"
                    className="button is-primary"
                    disabled={loading}
                  >
                    {loading
                      ? "Adding..."
                      : "Add Conceptual Question & First Version"}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="column is-full">
            <hr />
            <h2 className="subtitle is-4">Existing Conceptual Questions</h2>
            {loading && Object.keys(conceptualQuestions).length === 0 && (
              <p>Loading questions...</p>
            )}
            {!loading && Object.keys(conceptualQuestions).length === 0 && (
              <p>No conceptual questions found. Add one above!</p>
            )}
            <div>
              {Object.entries(conceptualQuestions).map(
                ([commonId, langVersions]) => (
                  <ConceptualQuestionManager
                    key={commonId}
                    commonQuestionId={commonId}
                    languageVersions={langVersions}
                    onAddLanguageVersion={this.handleAddLanguageVersion}
                    onUpdateLanguageVersion={this.handleUpdateLanguageVersion}
                    onDeleteLanguageVersion={this.handleDeleteLanguageVersion}
                  />
                )
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
}
